import { inject, observer } from 'mobx-react';
import React from 'react';
import { FullTerminal, Stores, TerminalStore } from '../../store';
import styled from '../../styles/styled-components';
import { keyCodes } from '../../util/keyCodes';

interface TerminalProps {
  terminal: FullTerminal;
  currentCommand: string;
  onCommandChange: (text: string) => void;
  onEntry: () => void;
  onBack: () => void;
  onForward: () => void;
  onAutocomplete: (forward: boolean, cursorIndex: number) => void;
  onResetAutocomplete: () => void;
  autoFocus?: boolean;
}
interface TerminalStoreProps {
  terminalStore: TerminalStore;
}

class TerminalComponent extends React.Component<TerminalProps, {}> {
  inputEntry: InputEntry | null;
  scrollableContainer: HTMLDivElement | null;

  componentDidUpdate(prevProps: TerminalProps) {
    if (
      this.props.terminal.descriptor.id !== prevProps.terminal.descriptor.id
    ) {
      this.focusInput();
    }

    if (this.scrollableContainer) {
      // ensure we can always see command prompt by always
      // scrolling to bottom on every re-render
      this.scrollableContainer.scrollTop = this.scrollableContainer.scrollHeight;
    }
  }

  isIndexSensitive(terminal: FullTerminal, index: number) {
    return terminal.lines[index] && terminal.lines[index].sensitive;
  }

  isHidden(terminal: FullTerminal) {
    return (
      terminal.lines[terminal.lines.length - 1] &&
      terminal.lines[terminal.lines.length - 1].sensitive
    );
  }

  focusInput = () => {
    if (this.inputEntry) this.inputEntry.setFocus();
  };

  render() {
    const {
      terminal,
      currentCommand,
      onCommandChange,
      onEntry,
      onBack,
      onForward,
      autoFocus,
      onAutocomplete,
      onResetAutocomplete,
      ...rest
    } = this.props;

    return (
      <TermContainer
        {...rest}
        innerRef={ref => (this.scrollableContainer = ref)}
      >
        <div>
          {terminal.lines.map((line, lineNo) => {
            return (
              <pre key={lineNo}>
                {this.isIndexSensitive(terminal, lineNo - 1)
                  ? ' '
                  : line.output}
              </pre>
            );
          })}
        </div>
        <LineContainer>
          <CurrentPath>
            {terminal.endOfCommand
              ? (terminal.descriptor.currentPath || '') + '>'
              : null}
          </CurrentPath>
          <InputEntry
            ref={ref => (this.inputEntry = ref)}
            value={currentCommand}
            onChange={onCommandChange}
            onEntry={onEntry}
            onBack={onBack}
            onForward={onForward}
            onAutocomplete={onAutocomplete}
            onResetAutocomplete={onResetAutocomplete}
            invisible={this.isHidden(terminal)}
            autoFocus={autoFocus}
          />
        </LineContainer>
        <EmptyFocusTrigger onClick={this.focusInput} />
      </TermContainer>
    );
  }
}

const TermContainer = styled.div`
  font-family: Menlo, Monaco, Consolas, monospace !important;
  height: 100%;
  overflow-y: auto;
  font-size: 14px;
  background-color: ${p => p.theme.terminalBg};
  padding: 10px;
  color: white;
  flex: 1;
  user-select: text;
  display: flex;
  flex-direction: column;

  pre {
    white-space: pre-wrap;
    word-break: normal;
  }
`;

const EmptyFocusTrigger = styled.div`
  flex-grow: 1;
`;

const LineContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 20px;
  height: 20px;
  width: 100%;
`;

const CurrentPath = styled.div`
  flex-shrink: 0;
`;

interface InputEntryProps {
  value: string;
  onChange: (text: string) => void;
  onEntry: () => void;
  onBack: () => void;
  onForward: () => void;
  onAutocomplete: (forward: boolean, cursorIndex: number) => void;
  onResetAutocomplete: () => void;
  autoFocus?: boolean;
  invisible?: boolean;
}

class InputEntry extends React.Component<InputEntryProps, {}> {
  input: HTMLTextAreaElement;

  handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.props.onChange(e.target.value);

  handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const {
      onEntry,
      onBack,
      onForward,
      onAutocomplete,
      onResetAutocomplete,
    } = this.props;

    if (e.keyCode != keyCodes.tab && e.keyCode != keyCodes.shift) {
      onResetAutocomplete();
    }

    switch (e.keyCode) {
      case keyCodes.upArrow: {
        e.preventDefault();
        this.setFocus();
        onBack();
        break;
      }
      case keyCodes.downArrow: {
        e.preventDefault();
        this.setFocus();
        onForward();
        break;
      }
      case keyCodes.enter: {
        e.preventDefault();
        this.setFocus();
        onEntry();
        break;
      }
      case keyCodes.tab: {
        e.preventDefault();
        this.setFocus();
        var input = e.currentTarget;
        if (input.selectionEnd == input.selectionStart) {
          onAutocomplete(!e.shiftKey, input.selectionStart);
        }
        break;
      }
      default:
        break;
    }
  };

  render() {
    const {
      value,
      onChange,
      onEntry,
      onBack,
      onForward,
      invisible,
      ...rest
    } = this.props;

    return (
      <Input
        {...rest}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
        invisible={invisible}
        innerRef={ref => (this.input = ref)}
        value={value}
        onChange={this.handleChange}
        onKeyDown={this.handleKeyDown}
      />
    );
  }

  setFocus() {
    if (this.input) {
      this.input.focus();
      this.input.scrollIntoView();
    }
  }
}

export const Terminal = inject<Stores, TerminalProps, TerminalStoreProps, {}>(
  ({ terminalStore }) => ({ terminalStore }),
)(observer(TerminalComponent)) as React.ComponentType<TerminalProps>;

type InputProps = { invisible?: boolean } & JSX.IntrinsicElements['input'];

const Input = styled.input`
  flex-grow: 1;
  outline: none;
  user-select: text;
  resize: none;
  background-color: transparent;
  padding: 0 7px;
  border: 0;
  margin: 0;
  max-height: 20px;
  color: white;
  font-family: Menlo, Monaco, Consolas, monospace !important;

  opacity: ${(p: InputProps) => (p.invisible ? 0 : 1)};
`;
